import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import RecipeDetailModal from './RecipeDetailModal';
import RecipeFormModal, { RecipeFormValues } from './RecipeFormModal';
import { sourceLabel } from '../constants/recipe';
import Icon from './Icon';
import Toast from './Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/recipes';

interface Recipe {
  id: number;
  recipe_id: string;
  name: string;
  cuisine: string;
  difficulty: string;
  prep_time: number;
  cook_time: number;
  servings: number;
  ingredients: { name: string; quantity: number; unit: string }[];
  instructions: string;
  source: string;
  times_used: number;
}

const emptyFormValues: RecipeFormValues = {
  name: '', cuisine: '', difficulty: 'Medium', prep_time: 0, cook_time: 0, servings: 4,
  ingredients: [{ name: '', quantity: 0, unit: '' }], instructions: '',
};

const MyRecipes: React.FC = () => {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [showChooser, setShowChooser] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [textInput, setTextInput] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [formTitle, setFormTitle] = useState('Add Recipe');
  const [formHint, setFormHint] = useState<string | undefined>(undefined);
  const [formValues, setFormValues] = useState<RecipeFormValues>(emptyFormValues);
  const [formSource, setFormSource] = useState('manual');

  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Recipe | null>(null);

  const errorDetail = (error: unknown): string | null =>
    axios.isAxiosError(error) ? error.response?.data?.error ?? null : null;

  const { data: recipes, isLoading } = useQuery({
    queryKey: ['my-recipes'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/my-recipes/`);
      return response.data as Recipe[];
    },
  });

  const importUrlMutation = useMutation({
    mutationFn: async (url: string) => {
      const response = await axios.post(`${API_URL}/my-recipes/import-url/`, { url });
      return response.data as RecipeFormValues;
    },
    onSuccess: (recipe) => {
      setShowUrlModal(false);
      setUrlInput('');
      setFormTitle('Review Imported Recipe');
      setFormHint("Review and edit before saving — nothing is saved yet.");
      setFormValues({ ...recipe, ingredients: recipe.ingredients.length ? recipe.ingredients : [{ name: '', quantity: 0, unit: '' }] });
      setFormSource('url_import');
      setFormOpen(true);
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Could not import a recipe from that URL.' });
    },
  });

  const importTextMutation = useMutation({
    mutationFn: async (text: string) => {
      const response = await axios.post(`${API_URL}/my-recipes/import-text/`, { text });
      return response.data as RecipeFormValues;
    },
    onSuccess: (recipe) => {
      setShowTextModal(false);
      setTextInput('');
      setFormTitle('Review Imported Recipe');
      setFormHint("Review and edit before saving — nothing is saved yet.");
      setFormValues({ ...recipe, ingredients: recipe.ingredients.length ? recipe.ingredients : [{ name: '', quantity: 0, unit: '' }] });
      setFormSource('text_import');
      setFormOpen(true);
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Could not find a recipe in that text.' });
    },
  });

  const createMutation = useMutation({
    mutationFn: async (values: RecipeFormValues) => {
      const response = await axios.post(`${API_URL}/my-recipes/create/`, { ...values, source: formSource });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-recipes'] });
      setFormOpen(false);
      setMessage({ type: 'success', text: 'Recipe saved to My Recipes.' });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error saving recipe.' });
    },
  });

  const unsaveMutation = useMutation({
    mutationFn: async (id: number) => {
      await axios.post(`${API_URL}/my-recipes/${id}/unsave/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-recipes'] });
      setRemoveTarget(null);
      setMessage({ type: 'success', text: 'Removed from My Recipes.' });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error removing recipe.' });
    },
  });

  const openManualForm = () => {
    setShowChooser(false);
    setFormTitle('Add Recipe');
    setFormHint(undefined);
    setFormValues(emptyFormValues);
    setFormSource('manual');
    setFormOpen(true);
  };

  const openUrlModal = () => {
    setShowChooser(false);
    setShowUrlModal(true);
  };

  const openTextModal = () => {
    setShowChooser(false);
    setShowTextModal(true);
  };

  const importing = importUrlMutation.isPending || importTextMutation.isPending;

  return (
    <div className="my-recipes">
      <div className="dashboard-header">
        <h1><Icon name="book" className="icon" />My Recipes</h1>
        <p>{recipes?.length || 0} saved recipes. Liked and saved recipes are considered first in future meal plans and "I'm Hungry" runs.</p>
        <button onClick={() => setShowChooser(true)} className="btn-primary" style={{ marginTop: '1rem' }}>
          + Add Recipe
        </button>
      </div>

      {isLoading ? (
        <div className="loading-screen">
          <div className="loading-spinner" />
          <p>Loading recipes...</p>
        </div>
      ) : !recipes || recipes.length === 0 ? (
        <div className="empty-state">
          <Icon name="crate" className="icon empty-icon" />
          <h2>No Saved Recipes Yet</h2>
          <p>Like a recipe from your Meal Plan or an "I'm Hungry" run, or add one yourself.</p>
          <button onClick={() => setShowChooser(true)} className="btn-primary" style={{ marginTop: '1rem' }}>
            + Add Recipe
          </button>
        </div>
      ) : (
        <div className="meals-grid">
          {recipes.map((recipe) => (
            <div key={recipe.id} className="meal-card">
              <div className="meal-header">
                <span className="recipe-source-badge">{sourceLabel(recipe.source)}</span>
                {recipe.times_used > 0 && (
                  <span className="meal-status status-completed">Used {recipe.times_used}x</span>
                )}
              </div>

              <h3 className="meal-name">{recipe.name}</h3>
              <div className="meal-meta">
                <span>{recipe.cuisine || 'Any cuisine'}</span>
                <span><Icon name="clock" className="icon icon-sm" /> {recipe.prep_time + recipe.cook_time} min</span>
              </div>

              <div className="meal-actions">
                <button onClick={() => setSelectedRecipe(recipe)} className="btn-secondary">
                  View Recipe
                </button>
                <button onClick={() => setRemoveTarget(recipe)} className="btn-danger">
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {message && (
        <Toast type={message.type} text={message.text} onDismiss={() => setMessage(null)} />
      )}

      {selectedRecipe && (
        <RecipeDetailModal recipe={selectedRecipe} onClose={() => setSelectedRecipe(null)} />
      )}

      {/* Add-method chooser */}
      {showChooser && (
        <div className="modal-overlay" onClick={() => setShowChooser(false)}>
          <div className="modal-content add-chooser" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowChooser(false)}>✕</button>
            <h2>Add Recipe</h2>
            <div className="add-chooser-options">
              <button type="button" className="add-chooser-option" onClick={openUrlModal}>
                <Icon name="link" className="icon add-chooser-icon" />
                <span className="add-chooser-label">Import from URL</span>
                <span className="add-chooser-desc">Paste a link to a recipe page</span>
              </button>
              <button type="button" className="add-chooser-option" onClick={openTextModal}>
                <Icon name="clipboard" className="icon add-chooser-icon" />
                <span className="add-chooser-label">Paste Recipe Text</span>
                <span className="add-chooser-desc">Paste in a recipe from anywhere else</span>
              </button>
              <button type="button" className="add-chooser-option" onClick={openManualForm}>
                <Icon name="edit" className="icon add-chooser-icon" />
                <span className="add-chooser-label">Enter Manually</span>
                <span className="add-chooser-desc">Type in your own recipe</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* URL import */}
      {showUrlModal && (
        <div className="modal-overlay" onClick={() => !importUrlMutation.isPending && setShowUrlModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowUrlModal(false)}>✕</button>
            <h2>Import from URL</h2>
            <div className="form-group">
              <label>Recipe URL</label>
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://example.com/recipe"
                autoFocus
              />
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowUrlModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => importUrlMutation.mutate(urlInput.trim())}
                disabled={!urlInput.trim() || importUrlMutation.isPending}
                className="btn-primary"
              >
                {importUrlMutation.isPending ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paste-text import */}
      {showTextModal && (
        <div className="modal-overlay" onClick={() => !importTextMutation.isPending && setShowTextModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowTextModal(false)}>✕</button>
            <h2>Paste Recipe Text</h2>
            <div className="form-group">
              <label>Recipe Text</label>
              <textarea
                className="recipe-instructions-input"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Paste a recipe here (ingredients, steps, whatever you have)..."
                rows={10}
                autoFocus
              />
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowTextModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => importTextMutation.mutate(textInput.trim())}
                disabled={!textInput.trim() || importTextMutation.isPending}
                className="btn-primary"
              >
                {importTextMutation.isPending ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Importing-in-progress overlay (AI extraction can take a few seconds) */}
      {importing && (
        <div className="modal-overlay">
          <div className="scan-loading">
            <div className="loading-spinner" />
            <p>Reading the recipe&hellip;</p>
          </div>
        </div>
      )}

      {formOpen && (
        <RecipeFormModal
          title={formTitle}
          hint={formHint}
          initialValues={formValues}
          submitting={createMutation.isPending}
          onSave={(values) => createMutation.mutate(values)}
          onCancel={() => setFormOpen(false)}
        />
      )}

      {removeTarget && (
        <div className="modal-overlay" onClick={() => setRemoveTarget(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setRemoveTarget(null)}>✕</button>
            <h2>Remove Recipe?</h2>
            <p>Remove "{removeTarget.name}" from My Recipes? It won't be considered for future meal plans until you save it again.</p>
            <div className="form-actions">
              <button type="button" onClick={() => setRemoveTarget(null)} className="btn-secondary">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => unsaveMutation.mutate(removeTarget.id)}
                disabled={unsaveMutation.isPending}
                className="btn-danger"
              >
                {unsaveMutation.isPending ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyRecipes;
